import { useContext } from 'react'

import * as z from 'zod'

import { isImageFileName } from '@/common/util/file'

import { BadgeType } from '@/components/view/atoms/badge/Badge'
import { MessageType } from '@/components/view/molecules/bar/MessageBar'

import { ToastMessageContext } from '@/components/container/utils/message/ToastMessageProvider'

export const StringLength = {
  NORMAL_MAX: 255,
  TEXT_MAX: 5000,
  HTML_MAX: 1000000,
  ID_MAX: 30,
  PASSWORD_MIN: 8
} as const

export type StringLength = (typeof StringLength)[keyof typeof StringLength]

export const NumberValue = {
  PRICE_MAX: 99999999,
  POINT_MAX: 99999999,
  TAX_RATE_MAX: 100,
  STOCK_MAX: 99999999,
  STOCK_MIN: -99999999
} as const

export type NumberValue = (typeof NumberValue)[keyof typeof NumberValue]

export const validatePrice = () => {
  return z.number().int().min(0).max(NumberValue.PRICE_MAX).nullable()
}

export const validateNegativePossiblePrice = () => {
  return z.number().int().min(-NumberValue.PRICE_MAX).max(NumberValue.PRICE_MAX).nullable()
}

export const validateRequiredPrice = () => {
  return z.number().int().min(0).max(NumberValue.PRICE_MAX)
}

export const validateQuantity = () => {
  return z.number().int().min(NumberValue.STOCK_MIN).max(NumberValue.STOCK_MAX)
}

export const validatePositiveQuantity = () => {
  return z.number().int().min(0).max(NumberValue.STOCK_MAX)
}

export const validatePhoneNumber = () => {
  return z.string().regex(/^(\d{2,4}\d{2,4}\d{4})$/, '電話番号の形式が正しくありません')
}

export const validateFaxNumber = () => {
  return z.string().regex(/^(\d{2,4}\d{2,4}\d{4})$/, 'FAX番号の形式が正しくありません')
}

export const validatePassword = () => {
  return z
    .string()
    .regex(
      /^((?=.*[a-zA-Z])(?=.*[0-9])|(?=.*[a-zA-Z])(?=.*[\-_/\*\+\.,!#\$%&\(\)~\|])|(?=.*[0-9])(?=.*[\-_/\*\+\.,!#\$%&\(\)~\|]))([a-zA-Z0-9\-_/\*\+\.,!#\$%&\(\)~\|]){8,32}$/,
      'パスワードは8文字以上で、英字・数字・記号のうち2種類以上を使用してください 。使用可能な記号は「-_/*+.,!#$%&()~|」です。'
    )
}

export const validateCustomerPassword = () => {
  return z
    .string()
    .regex(
      /^((?=.*[a-zA-Z])(?=.*[0-9])|(?=.*[a-zA-Z])(?=.*[\-_/\*\+\.,!#\$%&\(\)~\|])|(?=.*[0-9])(?=.*[\-_/\*\+\.,!#\$%&\(\)~\|]))([a-zA-Z0-9\-_/\*\+\.,!#\$%&\(\)~\|]){8,32}$/,
      'パスワードは8文字以上で、英字・数字・記号のうち2種類以上を使用してください 。使用可能な記号は「-_/*+.,!#$%&()~|」です。'
    )
}

export const validateCode = () => {
  return z
    .string()
    .regex(/^[a-zA-Z0-9-_]*$/, '利用できる文字は英数字と「-」「_」です。')
    .max(StringLength.NORMAL_MAX)
}

export const validateKana = () => {
  return z.string().regex(/^[ァ-ヶー　]*$/, 'カタカナで入力してください。')
}

export const DATE_BETWEEN_MESSAGE = '開始日時<終了日時となるよう入力してください。'
export const NUMBER_BETWEEN_MESSAGE = '最小値<最大値となるよう入力してください。'

export const CODE_UNIQUE_MESSAGE =
  '既に利用されている値です。利用されていない値を利用してください。'

export const PASSWORD_CAPTION = '半角英数記号8～32文字/2種混合'

export const PASSWORD_MASKING = '********'

export const validateMultipleBindValue = (zodString: z.ZodString) => {
  return zodString
    .refine((value) => !value.includes('`'), {
      message: 'バッククォート(`)は入力できません。'
    })
    .refine(
      (value) =>
        !value.startsWith(' ') &&
        !value.startsWith('　') &&
        !value.endsWith(' ') &&
        !value.endsWith('　'),
      {
        message: '最初と最後にはスペースは入力できません。'
      }
    )
}

export const validateMultipleBindValueByValue = (value: string) => {
  if (value.includes('`')) {
    return 'バッククォート(`)は入力できません。'
  }
  if (
    value.startsWith(' ') ||
    value.startsWith('　') ||
    value.endsWith(' ') ||
    value.endsWith('　')
  ) {
    return '最初と最後にはスペースは入力できません。'
  }
  return undefined
}

export let uniqueValidatedList: { code: string; result: boolean }[] = []

export const makeValidateCodeUnique = (
  findModel: (code: string) => Promise<string | number | undefined>
) => {
  return async (id: string | number | undefined, code: string): Promise<boolean> => {
    const validated = uniqueValidatedList.find((searched) => searched.code == code)
    if (validated) {
      return validated.result
    }

    const modelId = await findModel(code)
    const result = !modelId || (!!id && modelId == id)

    uniqueValidatedList.push({ code, result })

    return result
  }
}

export const refreshCodeUnique = () => {
  uniqueValidatedList = []
}

export const imageSchema = z.object({
  stored: z
    .object({
      imagePath: z.string()
    })
    .optional(),
  uploading: z.custom<File>().optional()
})

export type ImageSchemaType = z.infer<typeof imageSchema>

export const IMAGE_EXTENSION_INVALID_MESSAGE =
  'アップロード可能なファイル形式は.jpeg/.jpg/.png/.gif/.webpです。'

export const imageModelSchema = z.object({
  stored: z
    .object({
      id: z.string().readonly(),
      imagePath: z.string()
    })
    .optional(),
  uploading: z
    .custom<File>()
    .refine(
      (file) => {
        return isImageFileName(file.name)
      },
      { message: IMAGE_EXTENSION_INVALID_MESSAGE }
    )
    .optional()
})

export type ImageModelSchemaType = z.infer<typeof imageModelSchema>

export const useImagesValidation = () => {
  const { addMessage } = useContext(ToastMessageContext)

  return {
    validate: (files: File[]) => {
      const invalidExtensionExists = files.some((file) => {
        return !isImageFileName(file.name)
      })

      if (invalidExtensionExists) {
        addMessage({ text: IMAGE_EXTENSION_INVALID_MESSAGE, type: MessageType.CRITICAL })
      }
      return !invalidExtensionExists
    }
  }
}

export const fileModelSchema = z.object({
  stored: z
    .object({
      id: z.string().readonly(),
      imagePath: z.string().optional(),
      name: z.string()
    })
    .optional(),
  uploading: z.custom<File>().optional()
})

export type FileModelSchemaType = z.infer<typeof fileModelSchema>

// For Images
// export const imageModelSchemaPath = (image: ImageSchemaType | ImageModelSchemaType) => {
//   return image.stored
//     ? imagePath(image.stored.imagePath)
//     : image.uploading
//       ? URL.createObjectURL(image.uploading)
//       : undefined
// }

// export const fileModelSchemaImagePath = (image: FileModelSchemaType) => {
//   return image.stored?.imagePath
//     ? imagePath(image.stored.imagePath)
//     : image.uploading && isImageFileName(image.uploading.name)
//       ? URL.createObjectURL(image.uploading)
//       : undefined
// }

// export const imageModelSchemaPathList = (images: (ImageSchemaType | ImageModelSchemaType)[]) => {
//   return images.map((image) => {
//     return image ? imageModelSchemaPath(image) : undefined
//   })
// }

// export const singleImageConnectInput = (
//   schema: { image?: ImageSchemaType | null },
//   fileId: string | undefined,
//   preSchema?: { image?: ImageSchemaType | null }
// ): ConnectInput | undefined => {
//   const imageVars: ConnectInput = {
//     ...(fileId && {
//       connect: {
//         id: fileId
//       }
//     }),
//     ...(preSchema?.image?.stored &&
//       !schema.image?.stored &&
//       !fileId && {
//         disconnect: true
//       })
//   }

//   return Object.keys(imageVars).length > 0 ? imageVars : undefined
// }

export const multiMasterSchema = z
  .object({
    id: z.string(),
    name: validateMultipleBindValue(z.string().min(1).max(StringLength.NORMAL_MAX))
  })
  .array()

export type MultiMasterSchemaType = z.infer<typeof multiMasterSchema>

export const productTagMultiMasterSchema = z
  .object({
    id: z.string().optional(),
    name: validateMultipleBindValue(z.string().min(1).max(StringLength.NORMAL_MAX)),
    onlyAdmin: z.boolean().optional()
  })
  .array()

export type TagMultiMasterSchemaType = z.infer<typeof tagMultiMasterSchema>

export const tagMultiMasterSchema = z
  .object({
    id: z.string().optional(),
    name: validateMultipleBindValue(z.string().min(1).max(StringLength.NORMAL_MAX)),
    badgeType: z.custom<BadgeType>().optional()
  })
  .array()

export const singleMasterSchema = z
  .object({
    id: z.string().readonly().optional(),
    name: z.string().max(StringLength.NORMAL_MAX).nullable()
  })
  .nullable()

export const colorSchema = z.object({
  color: z.string().nullable()
})

export type ColorSchemaType = z.infer<typeof colorSchema>

export const statusSchema = colorSchema.extend({
  id: z.string().readonly().optional(),
  name: z.string().min(1).max(StringLength.NORMAL_MAX),
  code: validateCode(),
  displayName: z.string().max(StringLength.NORMAL_MAX),
  color: z.string().nullable(),
  triggerEnabled: z.boolean(),
  limitStatusIds: z.string().array()
})

export type StatusSchemaType = z.infer<typeof statusSchema>

export const variationValueSchema = z.object({
  valueName: z.string(),
  variationName: z.string()
})

export type VariationValueSchemaType = z.infer<typeof variationValueSchema>
